import React from 'react';
import { Participation } from '@app/api';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { CardHomeBase, StyleCardHome } from './CardHomeBase';
import { ApplyToStudies } from './ApplyToStudies';
import { Trans, useTranslation } from 'react-i18next';
import { EmptyState } from '../../Studies/components/EmptyState';
import InboxBlue from '@app/illu/Small/InboxBlue.svg';
import classnames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  globalContainer: {
    width: '100%',
  },
  cardContainer: {
    width: '100%',
    paddingLeft: theme.marginBase * 3,
    paddingRight: theme.marginBase * 3,
    paddingBottom: theme.marginBase * 2,
    gap: theme.marginBase * 2,
    display: 'inline-flex',
    justifyContent: 'stretch',
    overflowX: 'auto',
    overflowY: 'hidden',
    [theme.media.homeScreen]: {
      display: 'flex',
      flexDirection: 'column',
      padding: [0, theme.marginBase * 3],
    },
  },
  childrenContainer: {
    padding: 0,
  },
  childrenContainerNoParticipation: {
    padding: [0, theme.marginBase * 3],
  },
  titleContainer: {
    padding: [
      0,
      theme.marginBase * 3,
      theme.marginBase * 3,
      theme.marginBase * 3,
    ],
    display: 'inline-flex',
    alignItems: 'baseline',
  },
  titleContainerNoParticipation: {
    paddingLeft: '0px !important',
    paddingRight: '0px !important',
  },
  title: {
    ...theme.fonts.h4,
    textAlign: 'left',
    padding: [
      0,
      theme.marginBase * 3,
      theme.marginBase * 3,
      theme.marginBase * 3,
    ],
  },
  titleDigit: {
    ...theme.fonts.h2,
    textAlign: 'left',
    display: 'inline',
  },
  cardContainerG: {
    padding: 0,
    [theme.media.homeScreen]: {
      padding: 0,
    },
  },
  column: {
    gridColumn: 3,
    padding: [theme.marginBase * 5, 0, theme.marginBase * 3, 0],
  },
  emptyContainer: {
    backgroundColor: theme.colors.grayBackground,
  },
}));

interface Props {
  participationContacted?: Participation[];
}

export const ApplyToStudiesCard = ({ participationContacted }: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const participationLength = participationContacted?.length || 0;

  return (
    <CardHomeBase
      style={StyleCardHome.WHITE_PURPLE}
      icon="ri-mail-line"
      title={t('home.applyStudies.title')}
      className={classes.column}
      childrenClassName={classnames(classes.childrenContainer, {
        [classes.childrenContainerNoParticipation]: participationLength === 0,
      })}
    >
      <h1
        className={classnames(classes.title, {
          [classes.titleContainerNoParticipation]: participationLength === 0,
        })}
      >
        <Trans
          i18nKey={'home.applyStudies.match'}
          values={{ count: participationLength }}
          components={{ strong: <span className={classes.titleDigit} /> }}
        />
      </h1>
      {participationLength === 0 ? (
        <EmptyState
          illustration={InboxBlue}
          alt="Inbox"
          description={t('home.applyStudies.empty')}
          className={classes.emptyContainer}
        />
      ) : (
        <div className={classes.cardContainer}>
          {participationContacted?.map((participation, num) => {
            return participation.session ? (
              <ApplyToStudies
                key={participation.id}
                participation={participation}
                session={participation.session}
              />
            ) : null;
          })}
        </div>
      )}
    </CardHomeBase>
  );
};
